import React, { FC, useEffect, useState } from "react";

import {
  useGetClientFilesQuery,
  useSaveEmailAttachmentMutation,
} from "../../slices/DocumentsSlice";
import { FolderSelect } from "./FolderPicker";
import {
  ErrorText,
  errorText,
  FilesModal,
  INPUT,
  LABEL,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
} from "./ui/FilesUi";

/** Mail: save an email attachment into a client's files (folder and work item optional). */
export const SaveAttachmentModal: FC<{
  clients: { id: string; name: string }[];
  messageId: string;
  attachmentId: string;
  fileName: string;
  onClose: () => void;
  onSaved: (message: string) => void;
}> = ({ clients, messageId, attachmentId, fileName, onClose, onSaved }) => {
  const [customerId, setCustomerId] = useState(clients[0]?.id || "");
  const [folderId, setFolderId] = useState<string | null>(null);
  const [workId, setWorkId] = useState("");
  const [error, setError] = useState("");
  const { data: client, isFetching } = useGetClientFilesQuery(customerId, {
    skip: !customerId,
  });
  const [save, { isLoading }] = useSaveEmailAttachmentMutation();

  useEffect(() => {
    setFolderId(null);
    setWorkId("");
  }, [customerId]);

  return (
    <FilesModal
      title={"Save to client files"}
      subtitle={fileName}
      onClose={onClose}
      busy={isLoading}
      footer={
        <>
          <button
            type={"button"}
            className={SECONDARY_BUTTON}
            onClick={onClose}
            disabled={isLoading}>
            Cancel
          </button>
          <button
            type={"button"}
            className={PRIMARY_BUTTON}
            disabled={isLoading || !customerId}
            onClick={async () => {
              setError("");
              try {
                await save({
                  customerId,
                  folderId,
                  workId: workId || null,
                  messageId,
                  attachmentId,
                  fileName,
                }).unwrap();
                const where = folderId
                  ? client?.folders.find((f) => f.id === folderId)?.path
                  : null;
                onSaved(
                  `Saved to ${client?.name || "the client"}${
                    where ? ` › ${where}` : ""
                  }.`,
                );
              } catch (e) {
                setError(errorText(e));
              }
            }}>
            {isLoading ? "Saving…" : "Save"}
          </button>
        </>
      }>
      {clients.length === 0 ? (
        <p className={"text-[13.5px] text-[#4A5168]"}>
          The sender isn’t linked to a client in ProCharted. Add their email
          address to a client first.
        </p>
      ) : (
        <>
          <label htmlFor={"attachment-client"} className={LABEL}>
            Client
          </label>
          <select
            id={"attachment-client"}
            className={INPUT}
            value={customerId}
            onChange={(event) => setCustomerId(event.target.value)}>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <div className={"mt-3"}>
            <label htmlFor={"attachment-folder"} className={LABEL}>
              Folder
            </label>
            {isFetching && !client ? (
              <div className={"text-[13px] text-[#667085]"}>
                Loading folders…
              </div>
            ) : (
              <FolderSelect
                id={"attachment-folder"}
                folders={client?.folders || []}
                value={folderId}
                onChange={setFolderId}
              />
            )}
          </div>
          {!!client?.works.length && (
            <div className={"mt-3"}>
              <label htmlFor={"attachment-work"} className={LABEL}>
                Link to work item (optional)
              </label>
              <select
                id={"attachment-work"}
                className={INPUT}
                value={workId}
                onChange={(event) => setWorkId(event.target.value)}>
                <option value={""}>No work item</option>
                {client.works.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </>
      )}
      <ErrorText>{error}</ErrorText>
    </FilesModal>
  );
};
